import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Avatar,
  Skeleton,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Grid,
  Slider,
  TextField,
} from '@mui/material';
import { ReactComponent as SearchIcon } from '../../assets/icons/info/searchIcon.svg';
import { Link as MuiLink } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GlassButton,
  GlassIconButton,
  GlassProposalCard,
  GlassStatusChip,
  NeumorphicButton,
  GlassColors,
  ButtonTertiary,
  GlassModal,
  GlassModalContent,
} from '../../components/glassmorphism/GlassmorphismStyles';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getOrders,
  getHandymanSentProposals,
  getHandymanReceivedProposals,
  getHandymanAvailableOrders,
  updateHandymanProposal,
  sendHandymanProposal,
  searchOrdersByStatus,
  cancelOrder,
} from './actions';
import {
  OrderViewModel,
  CustomerOrderStatus,
  HandymanOrderStatus,
} from '../../configuration/api/apiSchema';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import CancelIcon from '@mui/icons-material/Cancel';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import NatureIcon from '@mui/icons-material/Nature';
import debounce from 'lodash/debounce';
import { trackEvent } from '../../analytics';

import { createOrGetChat } from './actions';
import { mapOrderStatusToString } from './OrderDetailsPage';
import { getOrderStatusColor } from './OrderDetailsPage';
import { Autocomplete } from '@react-google-maps/api';
import ProposalPriceModal from '../../components/ProposalPriceModal';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { getAuthData } from '../../helpers/localStorageHelpers';

import { ReactComponent as ClockIcon } from '../../assets/icons/info/clock.svg';
import { ReactComponent as EarlyBirdIcon } from '../../assets/icons/bages/early_bird.svg';
import { ReactComponent as VerifiedIcon } from '../../assets/icons/bages/varify_small.svg';
import { OrdersPageSkeleton } from './OrderPageSkeleton';
import SelectHandymanTrigger from './SelectHandymanTrigger';
import { selectHandymanForOrder } from './selectHandymanForOrder';
import { ConfirmModal } from './ConfirmModal';

// Helper to get order status color
// const getOrderStatusColor = (status: OrderStatus | undefined) => {
//   switch (status) {
//     case OrderStatus.New:
//       return 'info'; // Light blue - waiting for action
//     case OrderStatus.InProgress:
//       return 'success'; // Green - active work
//     case OrderStatus.MarkAsDone:
//       return 'warning'; // Orange/yellow - pending completion
//     case OrderStatus.Completed:
//       return 'primary'; // Dark blue - fully finished
//     default:
//       return 'default'; // Gray - fallback
//   }
// };

// Tab panel helper
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return value === index ? <Box sx={{ width: '100%' }}>{children}</Box> : null;
}

// Add this component for the skeleton loader
type OrdersSkeletonProps = {
  isDark?: boolean;
};
const OrdersSkeleton = ({ isDark }: OrdersSkeletonProps) => (
  <Box>
    {[1, 2, 3].map((index) => (
      <Card
        key={index}
        sx={{
          mb: 2,
          borderRadius: '32px',
          background: isDark
            ? GlassColors.dark.primaryBackground
            : GlassColors.light.primaryBackground,
          border: 0,
          boxShadow:
            '-2.82px -2.82px 8.49px 0px #FFFFFF4D,' +
            ' 14.15px 14.15px 22.64px -0.37px #0000001A,' +
            ' -17.54px -17.54px 28.66px 0px #FFFFFF0D,' +
            ' 10px 10px 28.66px -11.7px #00000033,' +
            ' 1.58px 1.58px 1.7px 0px #FFFFFF inset,' +
            ' -2.07px -2.64px 4.52px -1.51px #0000004D inset',
        }}
      >
        <CardHeader
          title={<Skeleton variant='text' width={200} height={24} />}
          subheader={<Skeleton variant='text' width={150} height={20} />}
        />
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Skeleton variant='text' width='100%' height={20} />
            <Box display='flex' gap={2}>
              <Skeleton variant='text' width={100} height={20} />
            </Box>
            <Box display='flex' gap={1} flexWrap='wrap'>
              <Skeleton
                variant='rectangular'
                width={80}
                height={24}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    ))}
  </Box>
);
type OrderStatus = CustomerOrderStatus | HandymanOrderStatus;
export function Description({ desc, isDark }: any) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const text = desc || 'No description provided';

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const overflow = el.scrollHeight > el.clientHeight + 1;
    setHasOverflow(overflow);
  }, [text]);

  return (
    <Typography
      ref={ref}
      sx={{
        fontFamily: 'Roboto, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '143%',
        letterSpacing: '0.17px',
        textAlign: 'justify',
        color: isDark
          ? GlassColors.dark.textPrimary
          : GlassColors.light.textPrimary,
        width: '100%',

        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        overflow: 'hidden',

        ...(hasOverflow
          ? {
              WebkitMaskImage:
                'linear-gradient(to bottom, black 0, transparent 100%)',
              maskImage:
                'linear-gradient(to bottom, black 0, transparent 100%)',
            }
          : {
              WebkitMaskImage: 'none',
              maskImage: 'none',
            }),
      }}
    >
      {text}
    </Typography>
  );
}

export const OrderCard = ({
  order,
  onChatWithCustomer,
  onChat,
  onCancel,
  role,
  onApply,
  tabContext,
  shouldVerify,
  applyingOrders = [],
  isDark,
  selectedStatus,
  handleSelectHandymanForOrder,
  handleProposalAction,
  handleRejectProposal,
  proposedDate,
  proposalId,
}: {
  order: OrderViewModel;
  onChat: (e: React.MouseEvent, handymanId: string) => void;
  onChatWithCustomer?: (e: React.MouseEvent, customerId: string) => void;
  onCancel: (id: string) => void;
  role: string;
  onApply?: (orderId: string) => void;
  shouldVerify: boolean;
  tabContext?: string;
  applyingOrders?: string[];
  isDark?: boolean;
  selectedStatus?: string;
  handleProposalAction?: (
    proposalId: string,
    action: (id: string) => void,
  ) => void;
  handleRejectProposal?: (proposalId: string | undefined) => void;
  handleSelectHandymanForOrder?: (orderId: string, handymanId: string) => void;
  proposedDate?: string | null;
  proposalId?: string;
}) => {
  const navigate = useNavigate();

  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showCancelOrderConfirm, setShowCancelOrderConfirm] = useState(false);
  // Determine which user info to display based on role
  const displayUser = role === 'customer' ? order.handyman : order.customer;
  const userTitle = role === 'customer' ? 'Handyman' : 'Customer';
  const getDisplayStatus = (order: OrderViewModel, role: string) => {
    return role === 'customer' ? order.customerStatus : order.handymanStatus;
  };

  const getDaysAgoLabel = (createdAtUtc: string): string => {
    const createdDate = new Date(createdAtUtc);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Sent today';
    if (days === 1) return 'Sent 1 day ago';
    return `Sent ${days} days ago`;
  };
  const isApplied = role === 'handyman' && selectedStatus === 'Applied';

  const displayStatus = getDisplayStatus(order, role);

  return (
    <>
      <Box
        onClick={() => order.id && navigate(`/orders/${order.id}`)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: { xs: '12px 16px 20px', sm: '16px 24px 24px' },
          gap: { xs: '12px', sm: '16px' },
          width: '100%',
          boxShadow: ` 
          -2.82px -2.82px 8.49px 0px #FFFFFF4D,
          14.15px 14.15px 22.64px -0.37px #0000001A,
          -17.54px -17.54px 28.66px 0px #FFFFFF0D,
          10px 10px 28.66px -11.7px #00000033,
          1.58px 1.58px 1.7px 0px #FFFFFF inset,
          -2.07px -2.64px 4.52px -1.51px #0000004D inset
        `,
          borderRadius: { xs: '24px', sm: '32px' },
          cursor: 'pointer',
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* Header section */}
        {/* Name and Service info section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '4px',
          }}
        >
          {/* Name and status badge row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: '6px', sm: '8px' },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                fontSize: '20px',
                lineHeight: '160%',
                letterSpacing: '0.15px',
                maxWidth: '100%',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
                flexBasis: { xs: '100%', tablet: 'auto' },
              }}
            >
              {order.name}
            </Typography>
            {/* Status Label */}
            <Box
              sx={{
                padding: '0 16px',
                background: getOrderStatusColor(displayStatus),
                boxShadow: `
                  0px 0px 34.77px 0px #0000000D inset,
                  0px -0.5px 0.25px 0px #00000080 inset,
                  0px 0.5px 0.75px 0px #FFFFFF inset,
                  0px 2.07px 3.73px 0px #0000004D,
                  0px 2.08px 2.48px -2px #00000066,
                  0px 3.08px 2px -2px #00000054
                `,
                borderRadius: '999px',
                ml: { xs: 'auto', tablet: 0 },
                order: { xs: -1, tablet: 0 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '22px',
                  height: '22px',
                  fontSize: '12px',
                  color: '#E5E5DC',
                }}
              >
                {tabContext === 'browse'
                  ? 'Awaiting proposals'
                  : mapOrderStatusToString(displayStatus, role)}
              </Typography>
            </Box>
          </Box>
          {/* Category name */}
          {order.category?.name && (
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '157%',
                letterSpacing: '0.1px',
                textAlign: 'left',
                color: isDark
                  ? GlassColors.dark.textSecondary
                  : GlassColors.light.textSecondary,
              }}
            >
              Service: {order.category.name}
            </Typography>
          )}
        </Box>
        {/* Info row with price, due date */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {/* Due date with clock icon */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: '0px',
              gap: '4px',

              'svg path': {
                fill: isDark ? GlassColors.accent : GlassColors.secondary,
              },
            }}
          >
            <ClockIcon />
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: '0.15px',
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
              }}
            >
              Due Date:{' '}
              {order.dueDateUtc
                ? new Date(order.dueDateUtc).toLocaleString('en-US', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : 'N/A'}
            </Typography>
          </Box>
          {/* Price */}
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '175%',
              textAlign: 'right',
              letterSpacing: '0.15px',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              '& span': {
                fontWeight: 400,
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
              },
            }}
          >
            <span>{role === 'handyman' ? 'You quoted: ' : 'Price: '}</span>
            {order.price ? `$${order.price}` : ' - '}
          </Typography>
        </Box>
        {/* Task description */}
        <Description isDark={isDark} desc={order.description} />
        {role === 'handyman' &&
          selectedStatus === 'Applied' &&
          proposedDate && (
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '175%',
                letterSpacing: '.15px',
              }}
            >
              {getDaysAgoLabel(proposedDate)}
            </Typography>
          )}
        {/* Actions with task based on selected status */}
        {selectedStatus === 'AwaitingProposals' && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              fontSize: '16px',
              lineHeight: '175%',
              letterSpacing: '.15px',
              fontWeight: 700,
              '& a': {
                position: 'relative',
                textDecoration: 'none',
                color: isDark ? GlassColors.accent : GlassColors.secondary,
              },
              '& a::before': {
                content: '""',
                display: 'block',
                width: '100%',
                height: '1px',
                backgroundColor: isDark
                  ? GlassColors.accent
                  : GlassColors.secondary,
                position: 'absolute',
                bottom: '6px',
                left: 0,
                transition: 'width .2s',
              },
              '& a:hover::before': {
                width: 0,
              },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              sx={{
                display: 'flex',
                fontSize: '16px',
                lineHeight: '175%',
                letterSpacing: '.15px',
                fontWeight: 700,
              }}
            >
              Searching for professionals...
            </Typography>

            <SelectHandymanTrigger
              variant='link'
              preselectedCategoryKey={order?.category?.key ?? undefined}
              isNotHandyman={true}
              excludeFilters={['price-range', 'location', 'name', 'categories']}
              address={order?.address ?? null}
              actionButtonTitle='Select'
              onSelect={(handymanId) =>
                handleSelectHandymanForOrder?.(order.id!, handymanId)
              }
            >
              <MuiLink
                sx={{
                  display: 'flex',
                  fontSize: '16px',
                  lineHeight: '175%',
                  letterSpacing: '.15px',
                  fontWeight: 700,
                }}
              >
                Browse Handyman
              </MuiLink>{' '}
            </SelectHandymanTrigger>
          </Box>
        )}
        {selectedStatus === 'ActionRequired' && order?.proposalsCount && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              fontSize: '16px',
              lineHeight: '175%',
              letterSpacing: '.15px',
              fontWeight: 700,
              fontFamily: 'Roboto, sans-serif',
              '& a': {
                position: 'relative',
                textDecoration: 'none',
                color: isDark ? GlassColors.accent : GlassColors.secondary,
              },
              '& a::before': {
                content: '""',
                display: 'block',
                width: '100%',
                height: '1px',
                backgroundColor: isDark
                  ? GlassColors.accent
                  : GlassColors.secondary,
                position: 'absolute',
                bottom: '6px',
                left: 0,
                transition: 'width .2s',
              },
              '& a:hover::before': {
                width: 0,
              },
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                fontSize: '16px',
                lineHeight: '175%',
                letterSpacing: '.15px',
                fontWeight: 700,
              }}
            >
              {`${order?.proposalsCount} ${order?.proposalsCount === 1 ? 'proposal' : 'proposals'} received`}
            </Typography>
            <Link
              to={`/proposals/order/${order.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              View
            </Link>
          </Box>
        )}
        {/* User info and chat link with user */}
        {(selectedStatus === 'InProgress' ||
          selectedStatus === 'Completed') && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '0px',
              gap: '16px',
              fontSize: '16px',
              lineHeight: '175%',
              letterSpacing: '.15px',
              fontWeight: 700,
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            {/* User logo and name with bage icons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {displayUser && (
                <Avatar
                  src={
                    displayUser?.avatarUrl || 'https://via.placeholder.com/40'
                  }
                  alt={
                    displayUser
                      ? `${displayUser.firstName} ${displayUser.lastName}`
                      : userTitle
                  }
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    backgroundColor: 'rgba(0, 36, 58, 0.8)',
                    color: '#E5E5DC',
                    backgroundSize: 'cover',
                    boxShadow: `
                  0px 0px 34.77px 0px #0000000D inset,
                  0px -0.5px 0.25px 0px #00000080 inset,
                  0px 0.5px 0.75px 0px #FFFFFF inset,
                  0px 2.07px 3.73px 0px #0000004D,
                  0px 2.08px 2.48px -2px #00000066,
                  0px 3.08px 2px -2px #00000054
                `,
                    borderRadius: '100px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (role === 'customer' && order.handyman?.id) {
                      navigate(`/browse-handyman/${order.handyman.id}`);
                    }
                  }}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  '& path': {
                    fill: isDark ? GlassColors.accent : GlassColors.secondary,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '175%',
                    letterSpacing: '0.15px',
                    color: isDark
                      ? GlassColors.dark.textPrimary
                      : GlassColors.light.textPrimary,
                  }}
                >
                  {displayUser
                    ? `${displayUser.firstName} ${displayUser.lastName}`
                    : `No ${userTitle.toLowerCase()} assigned`}
                </Typography>
                {displayUser?.isEarlyBird && <EarlyBirdIcon />}
                {(role === 'customer'
                  ? displayUser?.isStripeAccountVerified &&
                    displayUser?.kycStatus === 'Verified'
                  : displayUser?.kycStatus === 'Verified') && <VerifiedIcon />}
              </Box>
            </Box>
            {displayUser && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (!displayUser.id) {
                    return;
                  }
                  onChat(e, displayUser.id);
                }}
                sx={{
                  '&': {
                    position: 'relative',
                    textDecoration: 'none',
                    color: isDark ? GlassColors.accent : GlassColors.secondary,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: '100%',
                    height: '1px',
                    backgroundColor: isDark
                      ? GlassColors.accent
                      : GlassColors.secondary,
                    position: 'absolute',
                    bottom: '6px',
                    left: 0,
                    transition: 'width .2s',
                  },
                  '&:hover::before': {
                    width: 0,
                  },
                }}
              >
                {role === 'customer'
                  ? 'Chat with handyman'
                  : 'Chat with customer'}
              </Box>
            )}
          </Box>
        )}
        {/* Buttons row */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px',
            gap: '10px',
            width: '100%',
          }}
        >
          {/* Cancel Order button (tertiary style) - LEFT SIDE */}
          {role === 'handyman' &&
            tabContext === 'browse' &&
            onApply &&
            order.id && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                {/* Chat with Customer button */}
                {order.customer?.id && onChatWithCustomer && (
                  <ButtonTertiary
                    startIcon={<ChatIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (order.customer?.id) {
                        onChatWithCustomer(e, order.customer.id);
                      }
                    }}
                  >
                    Chat
                  </ButtonTertiary>
                )}
                {/* Apply button */}
                <NeumorphicButton
                  disabled={
                    order.id ? applyingOrders.includes(order.id) : false
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (shouldVerify) {
                      navigate('/verification');
                      return;
                    }
                    if (order.id) {
                      onApply(order.id);
                    }
                  }}
                  sx={{
                    padding: '4px 13px',
                    height: '32px',
                    '&&': { fontSize: '12px' },
                  }}
                >
                  {order.id && applyingOrders.includes(order.id)
                    ? 'Applying...'
                    : shouldVerify
                      ? 'Verify to apply'
                      : 'Apply'}
                </NeumorphicButton>
              </Box>
            )}
          {isApplied && (
            <ButtonTertiary
              onClick={(e) => {
                e.stopPropagation();
                setShowWithdrawConfirm(true);
              }}
              sx={{ width: { xs: '100%', mobile: 'auto' } }}
            >
              Withdraw Proposal
            </ButtonTertiary>
          )}
          {role === 'handyman' && selectedStatus === 'InProgress' && (
            <ButtonTertiary
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelOrderConfirm(true);
              }}
              sx={{ width: { xs: '100%', mobile: 'auto' } }}
            >
              Cancel Order
            </ButtonTertiary>
          )}
          {/* View Details button (secondary style) - RIGHT SIDE */}
          <NeumorphicButton
            onClick={(e) => {
              e.stopPropagation();
              order.id && navigate(`/orders/${order.id}`);
            }}
            sx={{
              '&': {
                padding: '4px 13px',
                height: '32px',
                fontSize: '12px',
                ml: 'auto',
                width: isApplied ? { xs: '100%', mobile: 'auto' } : 'auto',
              },
            }}
          >
            View Details
          </NeumorphicButton>
        </Box>
      </Box>
      {/* Withdraw proposal submit modal */}
      {/* <GlassModal
        open={showWithdrawConfirm}
        onClose={() => setShowWithdrawConfirm(false)}
        sx={{ zIndex: 999999 }}
      >
        <GlassModalContent sx={{ maxWidth: '400px' }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '124%',
              textAlign: 'center',
              letterSpacing: '.25px',
              mb: '24px',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
            }}
          >
            Are you sure?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <ButtonTertiary
              sx={{
                '&&': { fontSize: '14px', height: '32px', minWidth: '66px' },
              }}
              onClick={() => setShowWithdrawConfirm(false)}
            >
              No
            </ButtonTertiary>
            <NeumorphicButton
              sx={{
                '&&': { fontSize: '14px', height: '32px', minWidth: '66px' },
              }}
              onClick={() => proposalId && handleRejectProposal?.(proposalId)}
            >
              Yes
            </NeumorphicButton>
          </Box>
        </GlassModalContent>
      </GlassModal> */}
      {/* Cancel Order submit modal */}
      {/* <GlassModal
        open={showCancelOrderConfirm}
        onClose={() => setShowCancelOrderConfirm(false)}
        sx={{ zIndex: 999999 }}
      >
        <GlassModalContent sx={{ maxWidth: '400px' }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '124%',
              textAlign: 'center',
              letterSpacing: '.25px',
              mb: '24px',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
            }}
          >
            Are you sure?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <ButtonTertiary
              sx={{
                '&&': { fontSize: '14px', height: '32px', minWidth: '66px' },
              }}
              onClick={() => setShowCancelOrderConfirm(false)}
            >
              No
            </ButtonTertiary>
            <NeumorphicButton
              sx={{
                '&&': { fontSize: '14px', height: '32px', minWidth: '66px' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCancel(order.id!);
              }}
            >
              Yes
            </NeumorphicButton>
          </Box>
        </GlassModalContent>
      </GlassModal> */}
      <ConfirmModal
        open={showWithdrawConfirm}
        onClose={() => setShowWithdrawConfirm(false)}
        onConfirm={() => {
          if (proposalId) {
            handleRejectProposal?.(proposalId);
            setShowWithdrawConfirm(false);
          }
        }}
        isDark={isDark}
      />

      <ConfirmModal
        open={showCancelOrderConfirm}
        onClose={() => setShowCancelOrderConfirm(false)}
        onConfirm={() => {
          if (order.id) {
            onCancel(order.id);
            setShowCancelOrderConfirm(false);
          }
        }}
        isDark={isDark}
      />
    </>
  );
};

const OrdersPage = () => {
  // Theme mode
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Replace mapStateToProps with useSelector
  const {
    isLoading,
    orders,
    handymanProposals,
    handymanAvailableOrders,
    handymanProposalsLoading,
    handymanAvailableOrdersLoading,
    searchResults,
    searchLoading,
    searchError,
  } = useSelector((state: HandymanAppState) => state.order);
  const [filterAddress, setFilterAddress] = useState<string | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(10);
  const [displayRadiusKm, setDisplayRadiusKm] = useState<number>(10); // Separate state for slider UI
  const [autocompleteRef, setAutocompleteRef] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<
    OrderStatus | 'AwaitingProposals' | 'Applied'
  >(CustomerOrderStatus.AwaitingProposals);
  const [isAvailableOrdersShown, setIsAvailableOrdersShown] =
    useState<boolean>(false);

  const role = useSelector(
    (state: HandymanAppState) => state.user?.authData?.role ?? 'customer',
  );
  const currentUserId = useSelector(
    (state: HandymanAppState) => state.user?.currentUserDetails?.id,
  );
  const currentUserDetails = useSelector(
    (state: HandymanAppState) => state.user?.currentUserDetails,
  );

  const dispatch =
    useDispatch<ThunkDispatch<HandymanAppState, unknown, AnyAction>>();

  // Memoize action creators
  const memoizedGetOrders = useCallback(() => {
    dispatch(getOrders());
  }, [dispatch]);

  // const memoizedGetHandymanProposals = useCallback(() => {
  //   dispatch(getHandymanProposals());
  // }, [dispatch]);

  const memoizedGetHandymanSentProposals = useCallback(() => {
    dispatch(getHandymanSentProposals());
  }, [dispatch]);

  const memoizedGetHandymanReceivedProposals = useCallback(() => {
    dispatch(getHandymanReceivedProposals());
  }, [dispatch]);

  const memoizedGetHandymanAvailableOrders = useCallback(() => {
    dispatch(
      getHandymanAvailableOrders({
        languageKey: 'en',
      }),
    );
  }, [dispatch]);

  const memoizedUpdateHandymanProposal = useCallback(
    (orderProposalId: string, status: 'Confirmed' | 'Rejected') => {
      return dispatch(updateHandymanProposal(orderProposalId, status));
    },
    [dispatch],
  );

  const memoizedSendHandymanProposal = useCallback(
    (orderId: string, proposedPrice?: number, coverLetter?: string) => {
      return dispatch(
        sendHandymanProposal(orderId, proposedPrice, coverLetter),
      );
    },
    [dispatch],
  );

  const memoizedSearchOrdersByStatus = useCallback(
    (status: OrderStatus) => {
      // include proposals for Actions required
      const includeProposals = status === CustomerOrderStatus.ActionRequired;
      dispatch(searchOrdersByStatus(status, includeProposals, role));
      console.log(role);
    },
    [dispatch],
  );

  const [tabValue] = useState(0);
  const navigate = useNavigate();

  // Local state to track per-item loading for proposal updates and order applications
  const [updatingProposals, setUpdatingProposals] = useState<string[]>([]);
  const [applyingOrders, setApplyingOrders] = useState<string[]>([]);

  // Modal state for price proposals
  const [showProposalModal, setShowProposalModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderViewModel | null>(
    null,
  );

  // Create stable debounced function for API calls only (no state updates to prevent circular dependency)
  const debouncedFetchOrders = React.useMemo(
    () =>
      debounce(() => {
        dispatch(
          getHandymanAvailableOrders({
            languageKey: 'en',
          }),
        );
      }, 500), // Reduced to 500ms for better UX
    [dispatch],
  );

  // Create separate debounced function for updating radiusKm state
  const debouncedUpdateRadius = React.useMemo(
    () =>
      debounce((radius: number) => {
        setRadiusKm(radius);
      }, 500),
    [],
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedFetchOrders.cancel();
      debouncedUpdateRadius.cancel();
    };
  }, [debouncedFetchOrders, debouncedUpdateRadius]);

  // On mount - only for customer orders (no location dependency needed)
  useEffect(() => {
    // Always load "Active" orders for customers, regardless of location filters
    memoizedGetOrders();
  }, [memoizedGetOrders]); // Only depend on the memoized function, not location filters

  useEffect(() => {
    if (role === 'handyman') {
      setSelectedStatus('Applied');
      memoizedGetHandymanSentProposals();
    } else {
      setSelectedStatus(CustomerOrderStatus.AwaitingProposals);
      memoizedSearchOrdersByStatus(CustomerOrderStatus.AwaitingProposals);
    }
  }, [memoizedSearchOrdersByStatus, memoizedGetHandymanSentProposals, role]);

  // Set default address to handyman's location on mount
  useEffect(() => {
    if (role === 'handyman' && currentUserDetails?.serviceAreaAddress) {
      setFilterAddress(currentUserDetails.serviceAreaAddress);
    }
  }, [role, currentUserDetails?.serviceAreaAddress]);

  // For handyman role - handle location-based filtering
  useEffect(() => {
    if (role === 'handyman') {
      debouncedFetchOrders();
    }

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedFetchOrders.cancel();
      debouncedUpdateRadius.cancel();
    };
  }, [
    filterAddress,
    radiusKm,
    role,
    debouncedFetchOrders,
    debouncedUpdateRadius,
  ]);

  // Load proposals when tab changes for handyman
  useEffect(() => {
    if (role === 'handyman') {
      if (tabValue === 1) {
        // My Proposals tab - load sent proposals
        memoizedGetHandymanSentProposals();
      } else if (tabValue === 2) {
        // Customer Proposals tab - load received proposals
        memoizedGetHandymanReceivedProposals();
      }
    }
  }, [
    tabValue,
    role,
    memoizedGetHandymanSentProposals,
    memoizedGetHandymanReceivedProposals,
  ]);

  // const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  //   setTabValue(newValue);
  // };

  // const handleStatusChange = (event: SelectChangeEvent<string>) => {
  //   const value = event.target.value;
  //   setSelectedStatus(value as OrderStatus | 'all');

  //   if (value === 'all') {
  //     memoizedGetOrders();
  //   } else {
  //     memoizedSearchOrdersByStatus(value as OrderStatus);
  //   }
  // };

  const handleChatWithHandyman = async (
    e: React.MouseEvent,
    handymanId: string,
  ) => {
    e.stopPropagation(); // prevent parent container from also firing
    if (!currentUserId) return;
    try {
      // Wait for the createOrGetChat call to complete
      const chatId: any = await dispatch(
        createOrGetChat([currentUserId, handymanId]) as any,
      );

      // If it returned a valid chat ID, then navigate to that chat
      if (chatId) {
        navigate(`/chats/${chatId}`);
      } else {
        console.error('Failed to get chat ID from response');
      }
    } catch (error) {
      // Handle any errors if needed (e.g. show a toast)
      console.error('Failed to create or get chat:', error);
    }
  };

  // Handle chat with customer (for handyman viewing proposals)
  const handleChatWithCustomer = async (
    e: React.MouseEvent,
    customerId: string,
  ) => {
    e.stopPropagation();

    if (!currentUserId) return;

    try {
      const chatId: any = await dispatch(
        createOrGetChat([currentUserId, customerId]) as any,
      );
      if (chatId) {
        navigate(`/chats/${chatId}`);
      } else {
        console.error('Failed to get chat ID from response');
      }
    } catch (error) {
      console.error('Failed to create or get chat:', error);
    }
  };

  // Active Orders: cancel order
  const memoizedCancelOrder = useCallback(
    (orderId: string) => {
      return dispatch(cancelOrder(orderId));
    },
    [dispatch],
  );
  const handleCancelOrder = async (id: string) => {
    try {
      await memoizedCancelOrder(id);
      memoizedSearchOrdersByStatus(selectedStatus as OrderStatus);
    } catch (error) {
      console.error('Failed to cancel order:', error);
    }
  };

  // Navigate to order details
  // const handleOrderClick = (orderId: string) => {
  //   navigate(`/orders/${orderId}`);
  // };

  // Fill address with handyman's service area address
  const handleFillMyAddress = () => {
    if (currentUserDetails?.serviceAreaAddress) {
      setFilterAddress(currentUserDetails.serviceAreaAddress);
    }
  };

  // Helper to decide badge color for proposal status
  // const getStatusChipColor = (status: string) => {
  //   switch (status.toLowerCase()) {
  //     case 'confirmed':
  //       return 'success';
  //     case 'rejected':
  //       return 'error';
  //     default:
  //       return 'default';
  //   }
  // };

  // Handyman: Accept or Reject a proposal with loader per proposal
  const handleAcceptProposal = (proposalId: string) => {
    setUpdatingProposals((prev) => [...prev, proposalId]);
    memoizedUpdateHandymanProposal(proposalId, 'Confirmed').finally(() => {
      setUpdatingProposals((prev) => prev.filter((id) => id !== proposalId));
      memoizedGetOrders();
    });
  };

  const handleRejectProposal = (proposalId: string | undefined) => {
    if (!proposalId) return;
    setUpdatingProposals((prev) => [...prev, proposalId]);
    memoizedUpdateHandymanProposal(proposalId, 'Rejected').finally(() => {
      setUpdatingProposals((prev) => prev.filter((id) => id !== proposalId));
      memoizedGetOrders();
    });
  };

  // Handyman: Open modal for price proposal
  const handleApplyToOrder = (orderId: string) => {
    const order = handymanAvailableOrders.find((o) => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setShowProposalModal(true);
    }
  };

  // Handle modal submission with price
  const handleProposalSubmit = (
    orderId: string,
    proposedPrice?: number,
    coverLetter?: string,
  ) => {
    setApplyingOrders((prev) => [...prev, orderId]);

    memoizedSendHandymanProposal(orderId, proposedPrice, coverLetter)
      .then(() => {
        trackEvent('handyman_proposal_sent', {
          order_id: orderId,
          has_custom_price: String(!!proposedPrice),
          source: 'orders_page',
        });
      })
      .finally(() => {
        setApplyingOrders((prev) => prev.filter((id) => id !== orderId));
        setShowProposalModal(false);
        setSelectedOrder(null);

        memoizedGetHandymanAvailableOrders();
      });
  };

  // Handle modal close
  const handleProposalModalClose = () => {
    setShowProposalModal(false);
    setSelectedOrder(null);
  };

  // Fix the status comparison
  // const isActiveOrder = (status: OrderStatus | undefined): boolean => {
  //   return status === OrderStatus.InProgress;
  // };

  // Fix the proposal status check
  const isRequestedProposal = (status: string | undefined): boolean => {
    return status?.toLowerCase() === 'requested';
  };

  // Fix the proposal actions
  const handleProposalAction = (
    proposalId: string | undefined,
    action: (id: string) => void,
  ) => {
    if (proposalId) {
      action(proposalId);
    }
  };

  // Helper function to handle apply to order
  const handleApplyToOrderWithCheck = (orderId: string | undefined) => {
    if (orderId) {
      handleApplyToOrder(orderId);
    }
  };

  // Helper to get the orders to display based on current filter

  const authData = getAuthData();
  let isLoggedIn = !!authData;
  const shouldVerify =
    isLoggedIn &&
    ((currentUserDetails?.kycStatus !== 'Verified' &&
      currentUserDetails?.kycStatus !== undefined) ||
      (authData?.role === 'handyman' &&
        currentUserDetails?.isStripeAccountVerified === false));

  const shouldVerifyHandyman =
    authData?.role === 'handyman' &&
    (currentUserDetails?.kycStatus !== 'Verified' ||
      !currentUserDetails?.isStripeAccountVerified);

  type AppliedOrderViewModel = OrderViewModel & {
    proposedDate?: string | null;
    proposalId?: string;
  };

  const isApplied = role === 'handyman' && selectedStatus === 'Applied';
  const getDisplayStatus = (
    order: OrderViewModel | undefined,
    role: string,
  ) => {
    if (!order) return undefined;
    return role === 'customer' ? order.customerStatus : order.handymanStatus;
  };
  const appliedOrders: AppliedOrderViewModel[] =
    role === 'handyman'
      ? handymanProposals
          .filter((p) => p.status !== 'Rejected')
          .filter(
            (p) =>
              getDisplayStatus(p.order, role) === HandymanOrderStatus.Applied,
          )
          .filter((p): p is typeof p & { order: OrderViewModel } => !!p.order)
          .map((p) => ({
            ...p.order,
            proposedDate: p.proposedDate,
            proposalId: p.id,
          }))
      : [];

  const displayOrders = isApplied ? appliedOrders : searchResults.orders;

  const displayLoading = isApplied ? handymanProposalsLoading : searchLoading;

  const totalOrdersCount = isLoading ? undefined : orders.length;
  const hasOrders =
    role === 'handyman'
      ? handymanProposals.length > 0 || orders.length > 0
      : orders.length > 0;

  const handleSelectHandymanForOrder = async (
    orderId: string,
    handymanId: string,
  ) => {
    await selectHandymanForOrder({
      dispatch,
      orderId,
      handymanId,
      role,
      onSuccess: () => {
        memoizedSearchOrdersByStatus(selectedStatus as OrderStatus);
      },
    });
  };

  const orderStatusTabs =
    role === 'customer'
      ? [
          CustomerOrderStatus.AwaitingProposals,
          CustomerOrderStatus.ActionRequired,
          CustomerOrderStatus.InProgress,
          CustomerOrderStatus.Completed,
          CustomerOrderStatus.Canceled,
        ]
      : [
          HandymanOrderStatus.InProgress,
          HandymanOrderStatus.Completed,
          HandymanOrderStatus.Canceled,
        ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: { xs: '96px 24px 24px', sm: '136px 24px 48px 24px' },
        gap: { xs: '24px', sm: '48px' },
        minHeight: { xs: 'calc(100vh - 336px)', md: 'calc(100vh - 294px)' },
        width: '100%',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
      }}
    >
      {/* Orders Title */}
      <Typography
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          fontSize: '34px',
          lineHeight: '124%',
          textAlign: 'center',
          letterSpacing: '.25px',
          color: isDark ? GlassColors.accent : GlassColors.secondary,
          width: '100%',
          maxWidth: '956px',
        }}
      >
        My Tasks
      </Typography>
      {/* Main Content Container */}
      {/* TABS */}
      {/* {role === 'handyman' && (
        <GlassTabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ mb: 0, flexWrap: 'wrap' }}
        >
          <GlassTab label='Orders' sx={{ fontWeight: 700 }} />
          <GlassTab label='My Proposals' sx={{ fontWeight: 700 }} />
          <GlassTab label='Customer Proposals' sx={{ fontWeight: 700 }} />
          <GlassTab label='Browse job' sx={{ fontWeight: 700 }} />
        </GlassTabs>
      )} */}
      {!hasOrders && totalOrdersCount !== undefined && (
        <Box
          textAlign='center'
          sx={{ width: '100%', maxWidth: '956px', m: 'auto' }}
        >
          <Typography
            variant='h6'
            sx={{
              fonstFamily: 'Roboto',
              fontSize: '16px',
              lineHeight: '175%',
              letterSpacing: '.15px',
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.light.textSecondary,
            }}
          >
            There's nothing here yet
          </Typography>
          {role === 'customer' ? (
            <NeumorphicButton
              startIcon={<AddIcon sx={{ color: '#FFF' }} />}
              onClick={
                shouldVerify
                  ? () => navigate('/verification')
                  : () => navigate('/create-order')
              }
              sx={{
                alignSelf: 'center',
                padding: { xs: '8px 32px', sm: '8px 16px' },
                fontSize: { xs: '14px', md: '14px' },
                height: '40px',
                mt: '16px',
                minWidth: { xs: '140px', sm: 'auto' },
                '& .MuiButton-startIcon': { ml: 0 },
              }}
            >
              {shouldVerify ? 'Verify to create task' : 'Create New Task'}
            </NeumorphicButton>
          ) : (
            <>
              <NeumorphicButton
                startIcon={<SearchIcon />}
                onClick={() => setIsAvailableOrdersShown(true)}
                sx={{
                  alignSelf: 'center',
                  padding: { xs: '8px 32px', sm: '8px 16px' },
                  fontSize: { xs: '14px', md: '14px' },
                  height: '40px',
                  mt: '16px',
                  minWidth: { xs: '140px', sm: 'auto' },
                  '& .MuiButton-startIcon': { ml: 0 },
                  '& path': {
                    fill: '#fff',
                  },
                }}
              >
                Find Job
              </NeumorphicButton>
              {isAvailableOrdersShown && (
                <Box>
                  {handymanAvailableOrdersLoading ? (
                    <OrdersSkeleton />
                  ) : handymanAvailableOrders.length === 0 ? (
                    <Box textAlign='center' py={4}>
                      <Typography variant='h6' color='text.secondary'>
                        No available orders found
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginTop: '24px',
                      }}
                    >
                      {handymanAvailableOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onChat={handleChatWithHandyman}
                          onChatWithCustomer={handleChatWithCustomer}
                          onCancel={handleCancelOrder}
                          role={role}
                          shouldVerify={shouldVerifyHandyman}
                          onApply={handleApplyToOrderWithCheck}
                          tabContext='browse'
                          applyingOrders={applyingOrders}
                          isDark={isDark}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}{' '}
      {totalOrdersCount === undefined && <OrdersPageSkeleton isDark={isDark} />}
      {/* {<OrdersPageSkeleton isDark={isDark} />} */}
      {hasOrders && totalOrdersCount !== undefined && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: { xs: '20px', sm: '24px', md: '32px' },
            width: '100%',
            maxWidth: '956px',
            background: isDark ? '#1A1A1A' : '#E5E5DC',
            borderRadius: { xs: '32px', sm: '48px', md: '64px' },
          }}
        >
          {/* TAB PANEL: ACTIVE ORDERS */}
          {hasOrders && (
            <TabPanel value={tabValue} index={0}>
              {/* Filter buttons and Add Order button */}

              <Box display='flex' alignItems='center' mb={3} width='100%'>
                <Box
                  display='flex'
                  flexWrap='wrap'
                  justifyContent='space-between'
                  sx={{
                    width: '100%',
                  }}
                  gap={1}
                >
                  {' '}
                  {role === 'handyman' && (
                    <Button
                      onClick={() => {
                        setIsAvailableOrdersShown(false);
                        setSelectedStatus('Applied');
                        memoizedGetHandymanSentProposals();
                      }}
                      disableRipple
                      disabled={selectedStatus === 'Applied'}
                      sx={{
                        padding: '4px 24px',
                        minHeight: '40px',
                        background:
                          selectedStatus === 'Applied'
                            ? isDark
                              ? GlassColors.accent
                              : GlassColors.secondary
                            : isDark
                              ? GlassColors.dark.primaryBackground
                              : GlassColors.light.primaryBackground,
                        boxShadow: `
                      0px 0px 16px 0px #5C5C5C4D inset,
                      0px 0px 3px 0px #FFFFFF66 inset,
                      -1px -1px 0.5px -1px #FFFFFF inset,
                      1px 1px 0.5px -1px #FFFFFF66 inset,
                      -1px -1px 0px -0.5px #FFFFFF80 inset,
                      1px 1px 0px -0.5px #FFFFFF99 inset,
                      0px 1px 8px 0px #0000001F,
                      0px 0px 2px 0px #0000001A
                    `,
                        backdropFilter: 'blur(12px)',
                        borderRadius: '32px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',

                        textTransform: 'none',
                        '&': {
                          color:
                            selectedStatus === 'Applied'
                              ? isDark
                                ? GlassColors.light.textPrimary
                                : GlassColors.dark.textPrimary
                              : isDark
                                ? GlassColors.dark.textPrimary
                                : GlassColors.light.textPrimary,
                        },
                        '&:active': {
                          color: GlassColors.secondary,
                          background: isDark
                            ? GlassColors.dark.primaryBackground
                            : GlassColors.light.primaryBackground,
                          boxShadow: `
                        0px 0px 3px 0px #00E8E757,
                        1px 1px 0px -1px #FFFFFF inset,
                        1px 1px 0px -0.5px #E1E1E199,
                        0px 1px 4px 0px #0000004D inset,
                        0px 0px 2px 0px #0000001A inset
                        `,
                        },
                        '&:not(.Mui-disabled):not(:active):hover': {
                          background:
                            selectedStatus === 'Applied'
                              ? isDark
                                ? GlassColors.accent
                                : GlassColors.secondary
                              : isDark
                                ? GlassColors.dark.primaryBackground
                                : GlassColors.light.primaryBackground,
                          boxShadow: `
                        0px 0px 16px 0px #8484844D inset,
                        0px 0px 3px 0px #00E8E770 inset,
                        -1px -1px 0.5px -1px #FFFFFF inset,
                        1px 1px 0.5px -1px #FFFFFFB2 inset,
                        -1px -1px 1px -0.5px #FFFFFFA6 inset,
                        1px 1px 0px -0.5px #FFFFFF99 inset,
                        0px 1px 8px 0px #0000001F,
                        0px 0px 2px 0px #0000001A
                      `,
                          color: GlassColors.secondary,
                        },
                        '&.Mui-disabled': {
                          color: isDark
                            ? GlassColors.textSecondary
                            : GlassColors.primaryBackground,
                          '&:hover': {
                            backgroundColor: isDark
                              ? GlassColors.accent
                              : GlassColors.secondary,
                          },
                        },
                      }}
                    >
                      Apllied
                    </Button>
                  )}
                  {orderStatusTabs.map((status) => (
                    <Button
                      key={status}
                      onClick={() => {
                        setIsAvailableOrdersShown(false);
                        setSelectedStatus(status);
                        memoizedSearchOrdersByStatus(status);
                      }}
                      disableRipple
                      disabled={selectedStatus === status}
                      sx={{
                        padding: '4px 24px',
                        minHeight: '40px',
                        background:
                          selectedStatus === status
                            ? isDark
                              ? GlassColors.accent
                              : GlassColors.secondary
                            : isDark
                              ? GlassColors.dark.primaryBackground
                              : GlassColors.light.primaryBackground,
                        boxShadow: `
                      0px 0px 16px 0px #5C5C5C4D inset,
                      0px 0px 3px 0px #FFFFFF66 inset,
                      -1px -1px 0.5px -1px #FFFFFF inset,
                      1px 1px 0.5px -1px #FFFFFF66 inset,
                      -1px -1px 0px -0.5px #FFFFFF80 inset,
                      1px 1px 0px -0.5px #FFFFFF99 inset,
                      0px 1px 8px 0px #0000001F,
                      0px 0px 2px 0px #0000001A
                    `,
                        backdropFilter: 'blur(12px)',
                        borderRadius: '32px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',

                        textTransform: 'none',
                        '&': {
                          color:
                            selectedStatus === status
                              ? isDark
                                ? GlassColors.light.textPrimary
                                : GlassColors.dark.textPrimary
                              : isDark
                                ? GlassColors.dark.textPrimary
                                : GlassColors.light.textPrimary,
                        },
                        '&:active': {
                          color: GlassColors.secondary,
                          background: isDark
                            ? GlassColors.dark.primaryBackground
                            : GlassColors.light.primaryBackground,
                          boxShadow: `
                        0px 0px 3px 0px #00E8E757,
                        1px 1px 0px -1px #FFFFFF inset,
                        1px 1px 0px -0.5px #E1E1E199,
                        0px 1px 4px 0px #0000004D inset,
                        0px 0px 2px 0px #0000001A inset
                        `,
                        },
                        '&:not(.Mui-disabled):not(:active):hover': {
                          background:
                            selectedStatus === status
                              ? isDark
                                ? GlassColors.accent
                                : GlassColors.secondary
                              : isDark
                                ? GlassColors.dark.primaryBackground
                                : GlassColors.light.primaryBackground,
                          boxShadow: `
                        0px 0px 16px 0px #8484844D inset,
                        0px 0px 3px 0px #00E8E770 inset,
                        -1px -1px 0.5px -1px #FFFFFF inset,
                        1px 1px 0.5px -1px #FFFFFFB2 inset,
                        -1px -1px 1px -0.5px #FFFFFFA6 inset,
                        1px 1px 0px -0.5px #FFFFFF99 inset,
                        0px 1px 8px 0px #0000001F,
                        0px 0px 2px 0px #0000001A
                      `,
                          color: GlassColors.secondary,
                        },
                        '&.Mui-disabled': {
                          color: isDark
                            ? GlassColors.textSecondary
                            : GlassColors.primaryBackground,
                          '&:hover': {
                            backgroundColor: isDark
                              ? GlassColors.accent
                              : GlassColors.secondary,
                          },
                        },
                      }}
                    >
                      {mapOrderStatusToString(status)}
                    </Button>
                  ))}
                </Box>
              </Box>
              {searchError && selectedStatus !== 'AwaitingProposals' && (
                <Box textAlign='center' py={2} mb={2}>
                  <Typography variant='body2' color='error'>
                    Error searching orders: {searchError}
                  </Typography>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      setSelectedStatus('AwaitingProposals');
                      memoizedGetOrders();
                    }}
                    sx={{ mt: 1, borderRadius: 2 }}
                    size='small'
                  >
                    Show All Orders
                  </Button>
                </Box>
              )}
              {displayLoading ? (
                <OrdersSkeleton isDark={isDark} />
              ) : displayOrders.length === 0 ? (
                <Box textAlign='center'>
                  <Typography
                    variant='h6'
                    sx={{
                      fonstFamily: 'Roboto',
                      fontSize: '16px',
                      lineHeight: '175%',
                      letterSpacing: '.15px',
                      color: isDark
                        ? GlassColors.dark.textSecondary
                        : GlassColors.light.textSecondary,
                    }}
                  >
                    There's nothing here yet
                  </Typography>
                  {role === 'handyman' ? (
                    <>
                      <NeumorphicButton
                        startIcon={<SearchIcon />}
                        onClick={() => setIsAvailableOrdersShown(true)}
                        sx={{
                          alignSelf: 'center',
                          padding: { xs: '8px 32px', sm: '8px 16px' },
                          fontSize: { xs: '14px', md: '14px' },
                          height: '40px',
                          mt: '16px',
                          minWidth: { xs: '140px', sm: 'auto' },
                          '& .MuiButton-startIcon': { ml: 0 },
                          '& path': {
                            fill: '#fff',
                          },
                        }}
                      >
                        Find Job
                      </NeumorphicButton>
                      {isAvailableOrdersShown && (
                        <Box>
                          {handymanAvailableOrdersLoading ? (
                            <OrdersSkeleton />
                          ) : handymanAvailableOrders.length === 0 ? (
                            <Box textAlign='center' py={4}>
                              <Typography variant='h6' color='text.secondary'>
                                No available orders found
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                marginTop: '24px',
                              }}
                            >
                              {handymanAvailableOrders.map((order) => (
                                <OrderCard
                                  key={order.id}
                                  order={order}
                                  onChat={handleChatWithHandyman}
                                  onChatWithCustomer={handleChatWithCustomer}
                                  onCancel={handleCancelOrder}
                                  role={role}
                                  shouldVerify={shouldVerifyHandyman}
                                  onApply={handleApplyToOrderWithCheck}
                                  tabContext='browse'
                                  applyingOrders={applyingOrders}
                                  isDark={isDark}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <NeumorphicButton
                      startIcon={<AddIcon sx={{ color: '#FFF' }} />}
                      onClick={
                        shouldVerify
                          ? () => navigate('/verification')
                          : () => navigate('/create-order')
                      }
                      sx={{
                        alignSelf: 'center',
                        padding: { xs: '8px 32px', sm: '8px 16px' },
                        fontSize: { xs: '14px', md: '14px' },
                        height: '40px',
                        mt: '16px',
                        minWidth: { xs: '140px', sm: 'auto' },
                        '& .MuiButton-startIcon': { ml: 0 },
                      }}
                    >
                      {shouldVerify
                        ? 'Verify to create task'
                        : 'Create New Task'}
                    </NeumorphicButton>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    gap: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {isApplied
                    ? appliedOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onChat={handleChatWithHandyman}
                          onCancel={handleCancelOrder}
                          role={role}
                          isDark={isDark}
                          selectedStatus={selectedStatus}
                          shouldVerify={shouldVerify}
                          handleSelectHandymanForOrder={
                            handleSelectHandymanForOrder
                          }
                          handleRejectProposal={handleRejectProposal}
                          handleProposalAction={handleProposalAction}
                          proposedDate={order?.proposedDate}
                          proposalId={order?.proposalId}
                        />
                      ))
                    : displayOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onChat={handleChatWithHandyman}
                          onCancel={handleCancelOrder}
                          role={role}
                          isDark={isDark}
                          selectedStatus={selectedStatus}
                          shouldVerify={shouldVerify}
                          handleSelectHandymanForOrder={
                            handleSelectHandymanForOrder
                          }
                          handleRejectProposal={handleRejectProposal}
                          handleProposalAction={handleProposalAction}
                        />
                      ))}
                  {role === 'customer' && (
                    <NeumorphicButton
                      startIcon={<AddIcon sx={{ color: '#FFF' }} />}
                      onClick={
                        shouldVerify
                          ? () => navigate('/verification')
                          : () => navigate('/create-order')
                      }
                      sx={{
                        alignSelf: 'center',
                        padding: { xs: '8px 32px', sm: '8px 16px' },
                        fontSize: { xs: '14px', md: '14px' },
                        height: '40px',
                        mt: '32px',
                        minWidth: { xs: '140px', sm: 'auto' },
                        '& .MuiButton-startIcon': { ml: 0 },
                      }}
                    >
                      {shouldVerify
                        ? 'Verify to create task'
                        : 'Create New Task'}
                    </NeumorphicButton>
                  )}
                </Box>
              )}
            </TabPanel>
          )}

          {/* TAB PANEL: MY PROPOSALS */}
          <TabPanel value={tabValue} index={1}>
            {role === 'handyman' ? (
              <>
                {handymanProposalsLoading ? (
                  <OrdersSkeleton />
                ) : handymanProposals.length === 0 ? (
                  <Box textAlign='center' py={4}>
                    <Typography variant='h6' color='text.secondary'>
                      No proposals found
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1 }}
                    >
                      Browse available orders to send proposals to customers
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    {handymanProposals.map((proposal) => {
                      const disableActions = updatingProposals.includes(
                        proposal.id!,
                      );
                      return (
                        <GlassProposalCard
                          key={proposal.id}
                          className={proposal.status?.toLowerCase() || ''}
                        >
                          <CardHeader
                            avatar={
                              <Avatar
                                src={
                                  proposal.order?.customer?.avatarUrl ||
                                  'https://via.placeholder.com/40'
                                }
                                alt={
                                  proposal.order?.customer
                                    ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                                    : 'Customer'
                                }
                                sx={{
                                  width: 42,
                                  height: 42,
                                  border: '3px solid rgba(255, 255, 255, 0.3)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                  },
                                }}
                              />
                            }
                            title={
                              <Box
                                display='flex'
                                alignItems='center'
                                gap={1.5}
                                sx={{ position: 'relative', zIndex: 1 }}
                              >
                                <Typography
                                  variant='h6'
                                  sx={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                                    fontFamily: 'Montserrat',
                                    lineHeight: '160%',
                                    letterSpacing: '0.15px',
                                  }}
                                >
                                  {proposal.order?.name}
                                </Typography>
                                <GlassStatusChip
                                  label={proposal.status}
                                  size='small'
                                  status={proposal.status?.toLowerCase() || ''}
                                />
                              </Box>
                            }
                            subheader={
                              <Typography
                                variant='body2'
                                sx={{
                                  color: isDark ? '#D5D5D5' : '#585858',
                                  fontFamily: 'Roboto',
                                  fontWeight: 700,
                                  fontSize: '16px',
                                  lineHeight: '175%',
                                  letterSpacing: '0.15px',
                                }}
                              >
                                {proposal.order?.customer
                                  ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                                  : 'Unknown Customer'}
                              </Typography>
                            }
                            sx={{
                              position: 'relative',
                              zIndex: 1,
                            }}
                          />
                          <Divider
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                          />
                          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Box
                              display='flex'
                              flexDirection='column'
                              gap={2.5}
                            >
                              <Typography
                                variant='body1'
                                sx={{
                                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                                  fontFamily: 'Roboto',
                                  lineHeight: '150%',
                                  fontSize: '16px',
                                  letterSpacing: '0.15px',
                                }}
                              >
                                {proposal.order?.description ||
                                  'No description provided'}
                              </Typography>

                              <Stack
                                direction='row'
                                spacing={2.5}
                                alignItems='center'
                                flexWrap='wrap'
                              >
                                {proposal.order?.price && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <Box sx={{ display: 'inline' }} />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        lineHeight: '175%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    >
                                      £{proposal.order.price}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.dueDateUtc && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <AccessTimeIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      Due Date:{' '}
                                      {new Date(
                                        proposal.order.dueDateUtc,
                                      ).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.category?.name && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <NatureIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      {proposal.order.category.name}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.location && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <LocationOnIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      {proposal.order.location}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>

                              <Box
                                display='flex'
                                justifyContent='space-between'
                                alignItems='center'
                                gap={2}
                              >
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  gap={1.5}
                                >
                                  <GlassIconButton
                                    onClick={(e) => {
                                      const customerId =
                                        proposal.order?.customer?.id;
                                      if (customerId) {
                                        handleChatWithCustomer(e, customerId);
                                      }
                                    }}
                                    title='Chat with Customer'
                                    sx={{
                                      color: '#005CAD',
                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(0, 92, 173, 0.1)',
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    <ChatIcon />
                                  </GlassIconButton>
                                  {proposal.status?.toLowerCase() ===
                                    'requested' &&
                                    !disableActions && (
                                      <GlassButton
                                        variant='outlined'
                                        startIcon={<CancelIcon />}
                                        onClick={() =>
                                          handleProposalAction(
                                            proposal.id,
                                            handleRejectProposal,
                                          )
                                        }
                                        disabled={disableActions}
                                        sx={{
                                          borderColor: 'rgba(244, 67, 54, 0.3)',
                                          color: 'rgba(244, 67, 54, 0.9)',
                                          '&:hover': {
                                            borderColor:
                                              'rgba(244, 67, 54, 0.5)',
                                            backgroundColor:
                                              'rgba(244, 67, 54, 0.1)',
                                          },
                                          '&:disabled': {
                                            borderColor:
                                              'rgba(244, 67, 54, 0.2)',
                                            color: 'rgba(244, 67, 54, 0.5)',
                                          },
                                        }}
                                      >
                                        {disableActions ? (
                                          <CircularProgress
                                            size={20}
                                            color='inherit'
                                          />
                                        ) : (
                                          'Cancel'
                                        )}
                                      </GlassButton>
                                    )}
                                </Box>
                                <NeumorphicButton
                                  onClick={() =>
                                    navigate(`/orders/${proposal.order?.id}`)
                                  }
                                  sx={{
                                    padding: '8px 24px',
                                    height: '48px',
                                    minWidth: '120px',
                                  }}
                                >
                                  View Details
                                </NeumorphicButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </GlassProposalCard>
                      );
                    })}
                  </Box>
                )}
              </>
            ) : null}
          </TabPanel>

          {/* TAB PANEL: CUSTOMER PROPOSALS */}

          <TabPanel value={tabValue} index={2}>
            {role === 'handyman' ? (
              <>
                {handymanProposalsLoading ? (
                  <OrdersSkeleton />
                ) : handymanProposals.length === 0 ? (
                  <Box textAlign='center' py={4}>
                    <Typography variant='h6' color='text.secondary'>
                      No customer proposals found
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1 }}
                    >
                      Customers will send you proposals when they want to hire
                      you for their orders
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    {handymanProposals.map((proposal) => {
                      const disableActions =
                        proposal.status?.toLowerCase() !== 'requested' ||
                        updatingProposals.includes(proposal.id!);

                      return (
                        <GlassProposalCard
                          key={proposal.id}
                          className={proposal.status?.toLowerCase() || ''}
                        >
                          <CardHeader
                            avatar={
                              <Avatar
                                src={
                                  proposal.order?.customer?.avatarUrl ||
                                  'https://via.placeholder.com/40'
                                }
                                alt={
                                  proposal.order?.customer
                                    ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                                    : 'Customer'
                                }
                                sx={{
                                  width: 42,
                                  height: 42,
                                  border: '3px solid rgba(255, 255, 255, 0.3)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                  },
                                }}
                              />
                            }
                            title={
                              <Box
                                display='flex'
                                alignItems='center'
                                gap={1.5}
                                sx={{ position: 'relative', zIndex: 1 }}
                              >
                                <Typography
                                  variant='h6'
                                  sx={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                                    fontFamily: 'Montserrat',
                                    lineHeight: '160%',
                                    letterSpacing: '0.15px',
                                  }}
                                >
                                  {proposal.order?.name}
                                </Typography>
                                <GlassStatusChip
                                  label={proposal.status}
                                  size='small'
                                  status={proposal.status?.toLowerCase() || ''}
                                />
                              </Box>
                            }
                            subheader={
                              <Typography
                                variant='body2'
                                sx={{
                                  color: isDark ? '#D5D5D5' : '#585858',
                                  fontFamily: 'Roboto',
                                  fontWeight: 700,
                                  fontSize: '16px',
                                  lineHeight: '175%',
                                  letterSpacing: '0.15px',
                                }}
                              >
                                {proposal.order?.customer
                                  ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                                  : 'Unknown Customer'}
                              </Typography>
                            }
                            sx={{
                              position: 'relative',
                              zIndex: 1,
                            }}
                          />
                          <Divider
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                          />
                          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Box
                              display='flex'
                              flexDirection='column'
                              gap={2.5}
                            >
                              <Typography
                                variant='body1'
                                sx={{
                                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                                  fontFamily: 'Roboto',
                                  lineHeight: '150%',
                                  fontSize: '16px',
                                  letterSpacing: '0.15px',
                                }}
                              >
                                {proposal.order?.description ||
                                  'No description provided'}
                              </Typography>

                              <Stack
                                direction='row'
                                spacing={2.5}
                                alignItems='center'
                                flexWrap='wrap'
                              >
                                {proposal.order?.price && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <Box sx={{ display: 'inline' }} />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        lineHeight: '175%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    >
                                      £{proposal.order.price}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.dueDateUtc && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <AccessTimeIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      Due Date:{' '}
                                      {new Date(
                                        proposal.order.dueDateUtc,
                                      ).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.category?.name && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <NatureIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      {proposal.order.category.name}
                                    </Typography>
                                  </Box>
                                )}
                                {proposal.order?.location && (
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    gap={0.5}
                                  >
                                    <LocationOnIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: isDark ? '#00FFFF' : '#005CAD',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                                      }}
                                    >
                                      {proposal.order.location}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>

                              <Box
                                display='flex'
                                justifyContent='space-between'
                                alignItems='center'
                                gap={2}
                              >
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  gap={1.5}
                                >
                                  <GlassIconButton
                                    onClick={(e) => {
                                      const customerId =
                                        proposal.order?.customer?.id;
                                      if (customerId) {
                                        handleChatWithCustomer(e, customerId);
                                      }
                                    }}
                                    title='Chat with Customer'
                                    sx={{
                                      color: '#005CAD',
                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(0, 92, 173, 0.1)',
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    <ChatIcon />
                                  </GlassIconButton>
                                  {!disableActions &&
                                    isRequestedProposal(proposal.status) && (
                                      <>
                                        <GlassButton
                                          variant='outlined'
                                          onClick={() =>
                                            handleProposalAction(
                                              proposal.id,
                                              handleAcceptProposal,
                                            )
                                          }
                                          disabled={updatingProposals.includes(
                                            proposal.id!,
                                          )}
                                          sx={{
                                            borderColor:
                                              'rgba(76, 175, 80, 0.3)',
                                            color: 'rgba(76, 175, 80, 0.9)',
                                            '&:hover': {
                                              borderColor:
                                                'rgba(76, 175, 80, 0.5)',
                                              backgroundColor:
                                                'rgba(76, 175, 80, 0.1)',
                                            },
                                            '&:disabled': {
                                              borderColor:
                                                'rgba(76, 175, 80, 0.2)',
                                              color: 'rgba(76, 175, 80, 0.5)',
                                            },
                                          }}
                                        >
                                          {updatingProposals.includes(
                                            proposal.id!,
                                          ) ? (
                                            <CircularProgress
                                              size={20}
                                              color='inherit'
                                            />
                                          ) : (
                                            'Accept'
                                          )}
                                        </GlassButton>
                                        <GlassButton
                                          variant='outlined'
                                          onClick={() =>
                                            handleProposalAction(
                                              proposal.id,
                                              handleRejectProposal,
                                            )
                                          }
                                          disabled={updatingProposals.includes(
                                            proposal.id!,
                                          )}
                                          sx={{
                                            borderColor:
                                              'rgba(244, 67, 54, 0.3)',
                                            color: 'rgba(244, 67, 54, 0.9)',
                                            '&:hover': {
                                              borderColor:
                                                'rgba(244, 67, 54, 0.5)',
                                              backgroundColor:
                                                'rgba(244, 67, 54, 0.1)',
                                            },
                                            '&:disabled': {
                                              borderColor:
                                                'rgba(244, 67, 54, 0.2)',
                                              color: 'rgba(244, 67, 54, 0.5)',
                                            },
                                          }}
                                        >
                                          {updatingProposals.includes(
                                            proposal.id!,
                                          ) ? (
                                            <CircularProgress
                                              size={20}
                                              color='inherit'
                                            />
                                          ) : (
                                            'Reject'
                                          )}
                                        </GlassButton>
                                      </>
                                    )}
                                </Box>
                                <NeumorphicButton
                                  onClick={() =>
                                    navigate(`/orders/${proposal.order?.id}`)
                                  }
                                  sx={{
                                    padding: '8px 24px',
                                    height: '48px',
                                    minWidth: '120px',
                                  }}
                                >
                                  View Details
                                </NeumorphicButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </GlassProposalCard>
                      );
                    })}
                  </Box>
                )}
              </>
            ) : null}
          </TabPanel>

          {/* TAB PANEL: BROWSE ORDERS */}
          <TabPanel value={tabValue} index={3}>
            {role === 'handyman' ? (
              <Grid container spacing={3}>
                {/* Left Column - Filters */}
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Typography
                      variant='h6'
                      sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
                    >
                      Filters
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Autocomplete
                        onLoad={(autocomplete) =>
                          setAutocompleteRef(autocomplete)
                        }
                        onPlaceChanged={() => {
                          const place = autocompleteRef?.getPlace();
                          if (place?.formatted_address) {
                            setFilterAddress(place.formatted_address);
                          }
                        }}
                      >
                        <TextField
                          label='Search Address'
                          variant='outlined'
                          fullWidth
                          value={filterAddress ?? ''}
                          onChange={(e) => setFilterAddress(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                      </Autocomplete>
                      {currentUserDetails?.serviceAreaAddress && (
                        <Button
                          startIcon={<MyLocationIcon />}
                          onClick={handleFillMyAddress}
                          variant='outlined'
                          size='small'
                          fullWidth
                          sx={{ mt: 1 }}
                        >
                          Use My Location
                        </Button>
                      )}
                    </Box>

                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 1 }}
                    >
                      Search Radius: {displayRadiusKm}km{' '}
                      {handymanAvailableOrdersLoading &&
                      displayRadiusKm !== radiusKm
                        ? '(searching...)'
                        : ''}
                    </Typography>
                    <Slider
                      value={displayRadiusKm}
                      onChange={(e, value) => {
                        const newValue = value as number;
                        setDisplayRadiusKm(newValue);
                        // Trigger debounced state update (which will trigger useEffect -> API call)
                        if (role === 'handyman') {
                          debouncedUpdateRadius(newValue);
                        }
                      }}
                      min={0}
                      max={50}
                      step={0.5}
                      valueLabelDisplay='auto'
                    />
                  </Paper>
                </Grid>

                {/* Right Column - Orders List */}
                <Grid item xs={12} md={9}>
                  {handymanAvailableOrdersLoading ? (
                    <OrdersSkeleton />
                  ) : handymanAvailableOrders.length === 0 ? (
                    <Box textAlign='center' py={4}>
                      <Typography variant='h6' color='text.secondary'>
                        No available orders found
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%' }}>
                      {handymanAvailableOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onChat={handleChatWithHandyman}
                          onChatWithCustomer={handleChatWithCustomer}
                          onCancel={handleCancelOrder}
                          role={role}
                          shouldVerify={shouldVerifyHandyman}
                          onApply={handleApplyToOrderWithCheck}
                          tabContext='browse'
                          applyingOrders={applyingOrders}
                          isDark={isDark}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            ) : null}
          </TabPanel>
        </Box>
      )}
      {/* Proposal Price Modal */}
      {selectedOrder && (
        <ProposalPriceModal
          open={showProposalModal}
          order={selectedOrder}
          onClose={handleProposalModalClose}
          onSubmit={handleProposalSubmit}
          loading={applyingOrders.includes(selectedOrder.id!)}
        />
      )}
    </Box>
  );
};

export default OrdersPage;
